import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import CustomButton from '../components/Button';
import { getStatusColor } from '../utils/statusHelpers';
import styles from './StatusChangeModal.module.scss';

type SelectedItemDetail = {
  id: number;
  name: string;
  entryType: string;
  status?: string;
};

interface StatusChangeModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (newStatus: string, changeSubentries: boolean) => void;
  loading?: boolean;
  selectedStatus?: string;
  selectedItemsDetails?: SelectedItemDetail[];
  variant?: 'internal' | 'external';
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'NeedsReview':
      return 'Needs Review';
    case 'Updated':
      return 'Updated';
    case 'Approved':
      return 'Approved';
    default:
      return status;
  }
};

const getSelectionKindLabel = (items: SelectedItemDetail[]): string => {
  const types = new Set(items.map((item) => String(item.entryType)));
  if (types.size === 1 && types.has('File')) return 'files';
  if (types.size === 1 && types.has('Folder')) return 'folders';
  if (types.size === 1 && types.has('Project')) return 'projects';
  return 'items';
};

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  loading = false,
  selectedStatus: initialSelectedStatus,
  selectedItemsDetails,
  variant = 'internal',
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(initialSelectedStatus || 'NeedsReview');

  const itemNames = useMemo(
    () => (selectedItemsDetails ?? []).map((item) => item.name).filter(Boolean),
    [selectedItemsDetails]
  );

  const selectionKind = useMemo(
    () =>
      itemNames.length > 0
        ? getSelectionKindLabel(selectedItemsDetails ?? [])
        : 'items',
    [itemNames.length, selectedItemsDetails]
  );

  const summaryText = useMemo(() => {
    const toLabel = getStatusLabel(selectedStatus);
    const scope =
      itemNames.length === 1
        ? `the selected ${selectionKind.endsWith('s') ? selectionKind.slice(0, -1) : 'item'}`
        : `all selected ${selectionKind}`;

    return `The status of ${scope} will be changed to ${toLabel}.`;
  }, [selectedStatus, itemNames.length, selectionKind]);

  useEffect(() => {
    if (visible && initialSelectedStatus) {
      setSelectedStatus(initialSelectedStatus);
    }
  }, [visible, initialSelectedStatus]);

  const handleConfirm = () => {
    onConfirm(selectedStatus, true);
  };

  return (
    <Modal
      centered
      title={
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Change Status</span>
          <CloseOutlined onClick={onCancel} className={styles.closeIcon} />
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      className={`${styles.statusChangeModal} ${variant === 'external' ? styles.statusChangeModalExternal : ''}`}
      width={600}
    >
      <div className={styles.modalContent}>
        <p className={styles.mainText}>{summaryText}</p>

        <div className={styles.statusTarget}>
          <span className={`${styles.statusPill} ${styles.statusPillNew}`}>
            <span
              className={styles.statusDot}
              style={{ backgroundColor: getStatusColor(selectedStatus) }}
            />
            {getStatusLabel(selectedStatus)}
          </span>
        </div>

        {itemNames.length > 0 ? (
          <ul className={styles.itemList}>
            {itemNames.map((name, index) => (
              <li key={`${name}-${index}`} className={styles.listItem}>
                {name}
              </li>
            ))}
          </ul>
        ) : null}

        <div className={styles.actionButtons}>
          <CustomButton onClick={onCancel} className={styles.cancelButton}>
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleConfirm}
            loading={loading}
            className={styles.confirmButton}
          >
            Change Status
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default StatusChangeModal;
